/**
 * <DataGrid> — the platform's grid, as a control rather than a page (#959).
 *
 * ── What this is, and what it deliberately is not ──────────────────────────
 * Every property the console's lists earned — `role="grid"` / `treegrid`,
 * announced column headers with `aria-sort`, a roving tabindex over the rows,
 * keyboard entry into inline editing, the selection tile, the expansion row, the
 * lives HERE, once. What is NOT here is anything that knows where
 * rows come from or what a row means: no query hook, no saved view, no persona,
 * no route, no palette scope, no `@solum/sdk` import at all.
 *
 * That absence is the whole point. `apps/console`'s `ListView` was the only
 * component in the product that could render a grid, and an app cannot import
 * from another app — so `apps/portal` rendered every list as a `<ul>` of cards
 * and a customer administrator got a visibly poorer surface than an internal
 * user. The line between the two is not "console vs portal", it is **what a grid
 * IS** versus **what an application DOES with one**, and everything on the
 * second side of that line arrives here as a prop:
 *
 *   · rows and columns          — resolved by the host (a saved view, or a page)
 *   · what a row is CALLED      — `rowName`
 *   · sorting                   — `onSortColumn` + `column.sort`
 *   · the column ⋮ menu         — `renderColumnMenu`, so the host brings its own popover
 *   · a cell menu               — `renderCellMenu`
 *   · committing an edit        — `onCommitCell`, which RESOLVES `true` iff the
 *                                 server took it; the green flash is that answer,
 *                                 never an assumption
 *   · remembered column widths  — `widthOf` / `onResizeColumn` / `onResetColumn`
 *   · what a row expands INTO   — `renderExpansion`
 *   · row-level highlights      — `decorateRow`
 *   · the trailer under it      — `footer`
 *
 * Selection is CONTROLLED (`selection` + `onSelectionChange`) rather than owned
 * here, because the actions that consume it live in the collection's BAR, not
 * in the grid: from the first selected row, `CollectionWorkspace` replaces the
 * governed commands beside the mark with the inline selection group (#2375 —
 * the floating pill is gone). The host supplies the already-authorised actions
 * and the selected ids.
 *
 *   · grouped presentation rows — `grouping` (#2375, ADR F-84). The host hands
 *     the rows ALREADY ORDERED group-contiguous and says what each group is
 *     called and how many rows it holds in the WHOLE result; this component
 *     only draws the header rows. It never groups a page by itself, because a
 *     page grouped here would imply totals nobody measured.
 *
 * ── Why `@solum/ui` and not a new `packages/grid` ──────────────────────────
 * `GridBar` — the row of chrome that sits directly ABOVE this component — has
 * been here since #UX. Issue #959's measurement is literally "the shared package
 * holds the bar and not the thing the bar sits on". A separate package would
 * have to depend on this one anyway (Card, Button, Badge, Spinner, cx) and would
 * split the product's chrome across two packages, which is the same split one
 * level out. The invariant that made it safe to land here is the one stated
 * above: this file has no data dependency, so the control kit stays a control
 * kit.
 */
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { ChevronRight, EllipsisVertical, Filter as FilterGlyph, GripVertical, Pin } from 'lucide-react'
import { DatePicker } from '../controls/DatePicker'
import { cx } from '../controls/cx'
import { ColumnResizer } from './ColumnResizer'
import { MIN_COL_PX, MAX_COL_PX } from './columnWidths'
import s from './grid.module.css'

/** A row is whatever the host says it is — this component reads `id` and nothing else. */
export type GridRow = Record<string, unknown>

/** Context handed to a column's custom cell renderer. */
export interface GridCellCtx {
  editing: boolean
  flashing: boolean
  startEdit: () => void
  /** Returns the write when there is one, so a governed menu can wait. */
  commit: (value: unknown) => void | Promise<unknown>
  openMenu: (anchor: HTMLElement) => void
}

export interface GridColumn {
  /** the field key — also what `onSortColumn` / `renderColumnMenu` are told */
  key: string
  /** the human label, announced by the sort button */
  label: string
  /** the declared CSS width, before any remembered override */
  width?: string
  align?: 'left' | 'right'
  /** custom cell body; omit for the generic text renderer */
  cell?: (row: GridRow, ctx: GridCellCtx) => ReactNode
  editable?: boolean
  editKind?: 'text' | 'number' | 'date'
  /**
   * The smallest value a `number` edit may commit. Absent means 0, which is
   * what every number cell was clamped to before this existed.
   *
   * It exists because a floor is a DOMAIN rule, and zero is not everyone's.
   * A catalogue volume tier starts at a quantity, and `min_qty`'s floor is
   * ONE: a tier from zero is not a tier. The hardcoded `Math.max(0, …)` below
   * was the single reason the tier matrix stayed a private `<table>` (#2406
   * subtraction ledger, "the gap is a per-column coercion hook") — adopting
   * the grid would have let a price list be saved with one. Declarative on
   * purpose: a number, not a coercion callback, so the column says what its
   * floor IS rather than handing the grid code to run.
   */
  editMin?: number
  /** this column has a click-to-open cell menu — the body comes from `renderCellMenu` */
  hasMenu?: boolean
  /** the current sort state of THIS column, announced as `aria-sort` */
  sort?: 'ascending' | 'descending' | 'none'
  /** 1-based priority when more than one column participates in the order. */
  sortOrder?: number
  /**
   * Whether this column can be ordered by. Defaults to true wherever the host
   * gave an `onSortColumn`. Set false for a column that is not a value — an
   * actions column has nothing to sort, and a sort button over one is an
   * affordance that does nothing.
   */
  sortable?: boolean
  /** Whether the host's column menu applies. Defaults to true when the host
   * supplies one; action columns are not data fields and set this false. */
  menuable?: boolean
  /** a filter is active on this column (keeps its ⋮ visible) */
  filtered?: boolean
  /**
   * WHAT the filter on this column says, in words — e.g. "Status is any of
   * Open, Pending".
   *
   * `filtered` alone was conveyed by tinting the ⋮ brand-coloured, which is a
   * colour-only signal (WCAG 1.4.1) and says nothing at all to a screen reader:
   * a user hears "Column menu", sees a row count that does not match the total,
   * and has no way to learn why. Given this, the header draws a filter mark
   * beside the label and the ⋮ is NAMED with it.
   */
  filterSummary?: string
  /**
   * The column's role when an opted-in grid becomes record cards at compact
   * widths. This is presentation only: every value remains in the same row and
   * the grid keeps its keyboard and accessibility contract.
   */
  compact?: 'primary' | 'detail' | 'actions' | 'hidden'
  /**
   * What this column's text IS, in a row (#2406). `name` is the identifying
   * one; `supporting` is a secondary fact read after it.
   *
   * It is DATA on the column rather than a class the host puts on its own
   * cell body, because most of these cells are composites — an `IconTile`
   * plus a label, returned as a fragment. A fragment takes no `className`,
   * and the only wrapper that would carry one collapses `.cell`'s
   * `gap: var(--sp-2)` between the tile and the label. Eleven console hosts
   * had each solved this by hand with a private class.
   *
   * Deliberately NOT called `muted`: `GridRowDecoration.tone` already has a
   * `'muted'` that means something else entirely — `opacity: .72` on the
   * whole ROW. Two `muted`s at two levels meaning two things is a trap.
   *
   * `identifier` (#2375) is a code a person scans rather than reads — a SKU, a
   * reference, a number: muted, small and tabular so a column of them lines up.
   *
   * The `name` column (else the FIRST column) is also the one that FILLS the
   * row: its track is `minmax(<declared>, 1fr)`, so a long name is not
   * truncated while unused space sits at the end of the row. A remembered
   * width the person dragged still wins.
   */
  emphasis?: 'name' | 'supporting' | 'identifier'
}

/**
 * GROUPED ROWS (#2375, ADR F-84) — the host's answer, drawn.
 *
 * The rows passed alongside this are ALREADY ordered so that each group is
 * contiguous. A group that runs past the page boundary simply has its header
 * drawn again on the next page, with the same total — so the total must be
 * the group's count in the WHOLE authorised, searched and filtered result,
 * never the count on this page. A host that cannot say so must not pass this.
 */
export interface GridGrouping {
  /** The field the rows are grouped by — for the header's accessible name. */
  column: string
  /** The column's human label ("Status"). */
  columnLabel: string
  /** A stable key per group; rows with equal keys are one group. */
  keyOf: (row: GridRow) => string
  /** The group's label in the field's normal display formatting. */
  labelOf: (key: string) => string
  /** The group's count in the whole result; `null` only while it is loading. */
  totalOf: (key: string) => number | null
  /** Groups whose first rows are on an EARLIER page. */
  continued?: ReadonlySet<string>
}

/** Row-level highlighting, decided by the host (measures, dates, anything). */
export interface GridRowDecoration {
  /** Shared semantic emphasis; hosts choose the fact, the grid owns its shape. */
  tone?: 'warn' | 'crit' | 'muted'
  /** how many at-risk flags — rendered as the ⚠ badge when > 0 */
  flagCount?: number
  /** the title shared by the row and its flag badge */
  title?: string
}

/** The host owns the governed form and its write door; the grid owns reveal,
 * focus restoration and the space the form opens into. */
export interface DataGridProps {
  columns: GridColumn[]
  rows: GridRow[]
  /** What a row is CALLED — a screen reader announces this as the row's name. */
  rowName: (row: GridRow) => string
  /** Plural, lowercase — used for "Select all people". */
  itemsLabel: string
  /**
   * The presentation the person explicitly selected. `grid` stays a grid at
   * every width; `cards` is a card gallery at every width. This is distinct
   * from `compact`, the older responsive fallback used by non-switchable
   * surfaces.
   */
  presentation?: 'grid' | 'cards'
  /** Opt in to the legacy narrow-container card fallback. */
  /**
   * Responsive card fallback measured against this grid's own container.
   * `cards` keeps the established 640px threshold. `cards-wide` is for a
   * fact-dense row whose declared columns cannot remain readable at tablet and
   * high-zoom workspace widths; it reflows below 900px without changing the
   * ordinary desktop table or any existing consumer.
   */
  compact?: 'cards' | 'cards-wide'

  /** Enter / Space / click on a row. */
  onActivateRow?: (id: string) => void
  /** The row the host considers open (paints the brand rail). */
  activeRowId?: string
  /** The DOM id a row's `aria-controls` points at — the host's detail pane. */
  rowControlsId?: string

  onSortColumn?: (key: string, options?: { additive: boolean }) => void
  /** Reorder a data column. The host owns and persists the resulting order. */
  onMoveColumn?: (key: string, toIndex: number) => void
  /** Let data cells grow and wrap instead of truncating to one line. */
  wrapCells?: boolean
  /** The ⋮ menu body. Its presence is what puts a ⋮ on every column header. */
  renderColumnMenu?: (a: {
    column: string; anchor: HTMLElement; close: () => void
    /**
     * "Autosize" (#2375 §6.3): fit this column to its widest header or visible
     * cell and commit it through `onResizeColumn` — the same remembered width a
     * drag writes. Absent when the host keeps no widths (no inert item).
     */
    autosize?: () => void
  }) => ReactNode
  /** A cell menu's body, for columns declaring `hasMenu`. */
  renderCellMenu?: (a: {
    row: GridRow; column: string; anchor: HTMLElement
    close: () => void; commit: (value: unknown) => void | Promise<unknown>
  }) => ReactNode
  /**
   * Whether a menu OWNED BY THIS GRID is open.
   *
   * The console runs a window-level j/k triage handler that must stand down
   * while a menu is up. That guard used to read one `menu` state because the
   * grid and the page were one component; split, the page can no longer see it,
   * and a silent regression there is a keystroke that does two things at once.
   */
  onMenuOpenChange?: (open: boolean) => void

  /** The remembered width override for a column, in px, or null/undefined. */
  widthOf?: (column: string) => number | null | undefined
  /**
   * Told whenever the column HEADER stops (or starts) being drawn because the
   * grid reflowed to cards — an explicit cards lens, or a compact fallback at
   * its measured width. The header's controls are then `inert` (never
   * focusable under the cards), so the host must offer their verbs elsewhere —
   * the view-settings ⋮ (#2375 repair).
   */
  onHeadersHiddenChange?: (hidden: boolean) => void
  /** Present ⇒ every header carries a resize grip. */
  onResizeColumn?: (column: string, px: number) => void
  /**
   * "Pin column left" (#2375 §6.3, §11 `pinnedCols`): these columns render
   * FIRST, in this order, right after the lead, and stay put while the grid
   * scrolls sideways inside its card. Host-owned state, like widths.
   */
  pinnedColumns?: readonly string[]
  onResetColumn?: (column: string) => void

  /** Present ⇒ the selection column is drawn. Controlled by the host. */
  selection?: Set<string>
  onSelectionChange?: (next: Set<string>) => void

  /**
   * Presentation-only row pins. The host persists the ordered stable ids; this
   * component moves only rows already supplied by the host. Missing ids never
   * cause a fetch and therefore cannot widen the query/RLS boundary.
   */
  pinnedRowIds?: readonly string[]
  onPinnedRowIdsChange?: (next: string[]) => void

  /** Present ⇒ rows can expand and the grid becomes a `treegrid`. */
  renderExpansion?: (row: GridRow) => ReactNode
  /**
   * Narrow expansion to rows that have something honest to reveal. A host may
   * still control `expandedRowId` to show a row-owned form on a row with no
   * twist; this predicate governs the DISCLOSURE affordance and tree semantics.
   */
  canExpandRow?: (row: GridRow) => boolean
  /** Controlled accordion state. Omit to let the grid own it. */
  expandedRowId?: string | null
  onExpandedRowIdChange?: (id: string | null) => void
  /** Preserve a host's stable test contract while the shared grid owns the control. */
  expandTestId?: (row: GridRow) => string
  /**
   * What the twist SAYS it will do. The default is the console's relation band
   * ("Show what X is related to"); a host whose expansion is something else owes
   * its own wording, because the label is the only thing a screen-reader user
   * has to decide whether to open it.
   */
  expandLabel?: (row: GridRow, expanded: boolean) => string

  /**
   * Commit an inline edit. MUST resolve `true` iff the server took it — the
   * green flash is the grid SAYING the write landed, and a flash fired on a
   * refusal is a lie the next refetch quietly corrects (#877).
   */
  onCommitCell?: (row: GridRow, field: string, value: unknown) => Promise<boolean>

  decorateRow?: (row: GridRow) => GridRowDecoration | undefined

  /** Rendered under the grid — the host's own count line, paging, anything. */
  footer?: ReactNode

  /** Grouped presentation rows. See `GridGrouping` for the host's obligation. */
  grouping?: GridGrouping
  /** The column whose ⋮ menu is open, so its button can say `aria-expanded`. */
  openColumnMenu?: string | null
}

/**
 * The lead column's width, COMPUTED from how many slots it carries (#2375).
 *
 * It used to be three stepped tokens (`--grid-lead`, `--grid-lead-expand`,
 * `--grid-lead-wide`) that each had to be re-budgeted for a coarse pointer.
 * Now the slot is a token (`--g-slot`, which already grows to 44px on a coarse
 * pointer) and the width is arithmetic over it, so the column can never be
 * narrower than what it holds. Exported so a card presentation and a test read
 * the same expression.
 */
export function gridLeadWidth(slots: number): string {
  const n = Math.max(1, slots)
  return `calc(var(--g-inset) + ${n} * var(--g-slot) + ${n - 1} * var(--g-slot-gap) + var(--g-lead-pad))`
}

/**
 * The narrowest a sized column may be drawn while its header shows `controls`
 * boxes (label, move handle, ⋮): each is `--ctl-h-floor` (44px on a coarse
 * pointer, 0 on a fine one) plus the cell's gaps and inline padding. Exported
 * so a test reads the same expression.
 */
export function headerControlFloor(controls: number): string {
  const n = Math.max(1, controls)
  return `calc(${n} * var(--ctl-h-floor) + ${n - 1} * var(--sp-1) + 2 * var(--sp-3))`
}

/**
 * The narrowest one track may be drawn: a length is itself, `minmax(a, b)` is
 * `a`, and a flexible or intrinsic track (`1fr`, `auto`, `20%`) contributes 0.
 */
export function trackMinimum(track: string): string {
  const t = track.trim()
  if (t.startsWith('minmax(')) {
    let depth = 0
    for (let i = 7; i < t.length; i += 1) {
      if (t[i] === '(') depth += 1
      else if (t[i] === ')') depth -= 1
      else if (t[i] === ',' && depth === 0) return trackMinimum(t.slice(7, i))
    }
    return '0px'
  }
  return /^(\d+(?:\.\d+)?px|max\(.*\)|calc\(.*\))$/.test(t) ? t : '0px'
}

/**
 * What every row of the grid is at least as wide as: the sum of its tracks'
 * minimums. Each row is its own CSS grid; without this a row is only as wide
 * as the scroll box, its tracks overflow it, and a PINNED (sticky) cell — which
 * cannot leave its row — is carried off-screen once the grid scrolls past the
 * row's edge (measured on a phone, #2375). One shared value keeps every row the
 * same width, so the `1fr` tracks still line up row to row.
 */
export function gridRowMinimum(tracks: readonly string[]): string {
  return `calc(${tracks.map(trackMinimum).join(' + ') || '0px'})`
}

/** The track a column takes when it is the one that fills the row. */
export function fillTrack(declared: string): string {
  return /fr\b|minmax\(/.test(declared) ? declared : `minmax(${declared}, 1fr)`
}

export function DataGrid({
  columns: declaredColumns, rows, rowName, itemsLabel,
  presentation, compact,
  onActivateRow, activeRowId, rowControlsId,
  onSortColumn, onMoveColumn, wrapCells, renderColumnMenu, renderCellMenu, onMenuOpenChange,
  widthOf, onResizeColumn, onResetColumn, pinnedColumns = [], onHeadersHiddenChange,
  selection, onSelectionChange,
  pinnedRowIds = [], onPinnedRowIdsChange,
  renderExpansion, canExpandRow, expandedRowId, onExpandedRowIdChange, expandTestId,
  expandLabel, onCommitCell, decorateRow,
  footer, grouping, openColumnMenu,
}: DataGridProps) {
  /* PINNED COLUMNS FIRST (#2375). Every later decision — the template, the
     header, the cells, the aria-colindex — reads this order, so a pinned column
     is simply the first data column, made sticky below. A key that names no
     column is ignored rather than drawn empty. */
  const pinnedKeys = pinnedColumns.filter((key) => declaredColumns.some((c) => c.key === key))
  const pinnedSet = new Set(pinnedKeys)
  const columns = pinnedKeys.length === 0 ? declaredColumns : [
    ...pinnedKeys.map((key) => declaredColumns.find((c) => c.key === key)!),
    ...declaredColumns.filter((c) => !pinnedSet.has(c.key)),
  ]
  // Row focus for the roving tabindex (UX-02).
  const [activeRow, setActiveRow] = useState(0)
  const [editing, setEditing] = useState<{ id: string; field: string } | null>(null)
  const editCancelled = useRef(false) // Escape's blur must not commit (#2881)
  const [flash, setFlash] = useState<string | null>(null)
  const [menu, setMenu] = useState<{ kind: 'col' | 'cell'; anchor: HTMLElement; col: string; rowId?: string } | null>(null)
  const [draggingColumn, setDraggingColumn] = useState<string | null>(null)
  const [dragTarget, setDragTarget] = useState<number | null>(null)
  /**
   * The row whose expansion is open. Accordion by design: one per grid.
   * Otherwise every expanded row carries its own state and there is no honest
   * answer to "where am I".
   */
  const [uncontrolledExpandedRow, setUncontrolledExpandedRow] = useState<string | null>(null)
  const expandedRow = expandedRowId === undefined ? uncontrolledExpandedRow : expandedRowId
  const setExpandedRow = (next: string | null) => {
    if (expandedRowId === undefined) setUncontrolledExpandedRow(next)
    onExpandedRowIdChange?.(next)
  }

  /* Reported through a ref-guarded effect rather than inline, so the host's
     state update never happens during this component's render. */
  const notify = useRef(onMenuOpenChange)
  notify.current = onMenuOpenChange
  useEffect(() => { notify.current?.(menu != null) }, [menu])

  const canExpand = renderExpansion != null && rows.some((row) => canExpandRow?.(row) ?? true)
  const selectable = onSelectionChange != null
  const pinnable = onPinnedRowIdsChange != null
  /* A read-only answer is not an action. Keep one roving entry point only when
     the row can open, expand, select, or hand focus to an interactive cell. */
  const rowsNavigable = onActivateRow != null || canExpand || selectable || pinnable
    || columns.some((column) => column.editable || column.hasMenu)
  /* The lead column carries the twist and the selection tile. With neither, it
     would be an empty `columnheader` — a column with no name, announced as
     nothing, in front of every row. */
  const hasLead = canExpand || selectable || pinnable
  const sel = selection ?? EMPTY
  const pinRank = new Map(pinnedRowIds.map((id, index) => [id, index]))
  /* The host's group order, by first appearance. When grouped, pins rise to the
     top of THEIR group rather than of the page: lifting a pinned row out of its
     group would split the group and put a second header under the first. */
  const groupRank = new Map<string, number>()
  if (grouping) {
    for (const row of rows) {
      const key = grouping.keyOf(row)
      if (!groupRank.has(key)) groupRank.set(key, groupRank.size)
    }
  }
  /* Stable within both groups: pins follow their persisted order, unpinned rows
     retain the host's query order. No off-page pin is materialised. */
  const orderedRows = rows
    .map((row, index) => ({
      row, index, pin: pinRank.get(String(row.id)),
      group: grouping ? groupRank.get(grouping.keyOf(row)) ?? 0 : 0,
    }))
    .sort((a, b) => {
      if (a.group !== b.group) return a.group - b.group
      if (a.pin != null && b.pin != null) return a.pin - b.pin
      if (a.pin != null) return -1
      if (b.pin != null) return 1
      return a.index - b.index
    })
    .map(({ row }) => row)
  /* What is drawn, in order, with each row's 1-based `aria-rowindex` (the
     header is row 1). Group headers and an open expansion are rows too, and a
     grid that numbered only the records would misreport position to a screen
     reader for every row below one of them. */
  type LayoutEntry =
    | { kind: 'group'; key: string; at: number; rowIndex: number }
    | { kind: 'row'; row: GridRow; index: number; rowIndex: number }
  const layout: LayoutEntry[] = []
  let nextRowIndex = 2
  let previousGroup: string | null = null
  orderedRows.forEach((row, index) => {
    if (grouping) {
      const key = grouping.keyOf(row)
      if (key !== previousGroup) {
        layout.push({ kind: 'group', key, at: index, rowIndex: nextRowIndex })
        nextRowIndex += 1
        previousGroup = key
      }
    }
    layout.push({ kind: 'row', row, index, rowIndex: nextRowIndex })
    nextRowIndex += expandedRow === String(row.id) ? 2 : 1
  })
  const renderedRowCount = nextRowIndex - 2
  const orderedIds = orderedRows.map((d) => String(d.id))
  const selectedOnPage = orderedIds.filter((id) => sel.has(id)).length
  const allOnPageSelected = orderedRows.length > 0 && selectedOnPage === orderedRows.length
  const selectAllRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedOnPage > 0 && !allOnPageSelected
    }
  }, [selectedOnPage, allOnPageSelected])
  // A shorter terminal page can replace a longer page while the roving index is
  // near its end. Clamp the rendered tab stop immediately so the new page never
  // has zero keyboard-entry rows (#882).
  const activeRowOnPage = Math.max(0, Math.min(activeRow, orderedRows.length - 1))

  const declaredWidth = (c: GridColumn) => c.width ?? '120px'
  /* The px number a resize gesture starts from. A declared width may be `1fr` or
     `20%`, which has no pixel value until it is laid out — so the grab falls back
     to the floor when no override and no px literal is available. */
  const pxWidth = (c: GridColumn): number => {
    const set = widthOf?.(c.key)
    if (set != null) return set
    const n = /^(\d+(?:\.\d+)?)px$/.exec(declaredWidth(c))
    return n ? Number(n[1]) : 120
  }
  /* The lead column's width is ARITHMETIC over tokens, never a literal: the
     slot token grows to 44px on a coarse pointer, and an inline pixel width
     cannot answer a media query. Writing the pixels here is how the expansion
     twist came to be 18px wide on a phone (#1466). The same value is published
     as `--grid-active-lead` for the card recipe's corner seat. */
  const leadControls = Number(canExpand) + Number(selectable) + Number(pinnable)
  const leadWidth = gridLeadWidth(leadControls)
  /* THE FILL (#2375 §8): the `emphasis: 'name'` column, else the first — UNLESS
     the host already declared a flexible track and flagged no name. Then that
     track IS the fill, and handing the first column a second `1fr` split the
     row between them: a 130px due date took half the width and the milestone
     title beside it overflowed (rogue census, /tenders/tnd1). */
  const hostFill = columns.find((c) => /fr\b/.test(declaredWidth(c)))
  const primaryKey = (columns.find((c) => c.emphasis === 'name') ?? hostFill ?? columns[0])?.key
  /* THE COARSE HEADER FLOOR (#2375 §6): a header cell holds its label, and the
     move handle and ⋮ where the host offers them — each a 44px box on a coarse
     pointer. A 120px column cannot hold three, so its ⋮ spilled into the next
     header cell, which took the tap: on a phone the column menu was unreachable.
     A px track is floored at what its header holds; the floor is arithmetic over
     `--ctl-h-floor`, 0 on a fine pointer, so desktop tracks are unchanged. */
  const headerFloor = headerControlFloor(1 + Number(Boolean(onMoveColumn)) + Number(Boolean(renderColumnMenu)))
  const floored = (track: string) => /^\d+(?:\.\d+)?px$/.test(track) ? `max(${track}, ${headerFloor})` : track
  const colTemplate = columns.map((c) => {
    const w = widthOf?.(c.key)
    if (w != null) return floored(`${w}px`)
    return c.key === primaryKey ? fillTrack(floored(declaredWidth(c))) : floored(declaredWidth(c))
  })
  /* The priority number is information only when there is more than one key;
     a lone "1" beside the only arrow says nothing (#2375). */
  const multiSort = columns.filter((c) => (c.sortOrder ?? 0) > 0 && c.sort && c.sort !== 'none').length >= 2
  const template = (hasLead ? [leadWidth, ...colTemplate] : colTemplate).join(' ')
  const rowMinimum = gridRowMinimum(hasLead ? [leadWidth, ...colTemplate] : colTemplate)
  /** 1-based column index of the first DATA column. */
  const firstCol = hasLead ? 2 : 1

  const moveColumnByKey = (key: string, toIndex: number) => {
    const from = columns.findIndex((column) => column.key === key)
    const clamped = Math.max(0, Math.min(columns.length - 1, toIndex))
    if (from < 0 || from === clamped) return
    /* The host's index is into ITS order, not the pinned-first display order. */
    const target = columns[clamped]?.key
    const declaredTo = target == null ? clamped : declaredColumns.findIndex((column) => column.key === target)
    onMoveColumn?.(key, declaredTo)
  }

  /* Closing a menu RETURNS FOCUS to the control that opened it. Without this a
     keyboard user who opens a column ⋮, applies a filter and closes is left on
     `<body>`: the roving tabindex is gone and the next Tab restarts from the top
     of the document.

     ── why this is a SELECTOR and not the anchor element ─────────────────────
     Two straightforward implementations were tried in the browser and both left
     focus on `<body>`, for the same reason and it is worth stating: **applying a
     filter changes the query key, the host's boundary swaps the grid for a
     spinner, and this component UNMOUNTS.** So the anchor node is destroyed, and
     a ref holding it — or holding anything at all — dies with the instance that
     held it. Instrumenting it is how that was established rather than guessed:
     `closeMenu` ran with a live anchor, and the effect that was to use it ran on
     a FRESH instance whose ref was already null.

     What survives a remount is the DOM contract: the ⋮ carries `data-colmenu`
     and an interactive cell carries `data-cell`, both stable across the
     round trip. The request therefore lives at module scope, is consumed by
     whichever instance next renders the matching node, and is scoped to THIS
     grid's own root so a second grid on the page cannot answer it. The stamp is
     there so a request abandoned by a navigation cannot steal focus from a page
     that had nothing to do with it. */
  const rootRef = useRef<HTMLDivElement>(null)
  const headRef = useRef<HTMLDivElement>(null)
  /* WHERE each pinned column sticks: the sum of the RESOLVED tracks before it
     (the lead, then earlier pinned columns), read from the header row's computed
     `grid-template-columns` — never a guessed width, because a pinned fill
     column is `minmax(…, 1fr)` and only the browser knows what that came to. */
  const [pinLeft, setPinLeft] = useState<Record<string, number>>({})
  const pinSignature = pinnedKeys.join('|')
  useLayoutEffect(() => {
    const head = headRef.current
    if (!head || pinnedKeys.length === 0) { setPinLeft((current) => (Object.keys(current).length ? {} : current)); return }
    const measure = () => {
      const tracks = getComputedStyle(head).gridTemplateColumns.split(' ').map((t) => Number.parseFloat(t) || 0)
      const offset = hasLead ? 1 : 0
      const next: Record<string, number> = {}
      let left = hasLead ? tracks[0] ?? 0 : 0
      pinnedKeys.forEach((key, index) => {
        next[key] = left
        left += tracks[index + offset] ?? 0
      })
      setPinLeft((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next)
    }
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(head)
    return () => observer?.disconnect()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinSignature, template, hasLead])
  const lastPinned = pinnedKeys[pinnedKeys.length - 1]
  const pinnedCellProps = (key: string) => pinnedSet.has(key) ? {
    'data-pinned-col': '',
    'data-pinned-edge': key === lastPinned ? '' : undefined,
    style: { insetInlineStart: pinLeft[key] ?? 0 },
  } : {}
  /* AUTOSIZE (#2375 §6.3): the widest CONTENT among this column's header and
     drawn cells — a Range over each cell's contents, because `scrollWidth`
     never reports less than the box and so could only ever grow a column —
     plus the cell's own inline padding, clamped to the resize bounds, committed
     through the same `onResizeColumn` a drag uses. */
  const autosizeColumn = (key: string) => {
    const host = rootRef.current
    if (!host || !onResizeColumn) return
    let widest = 0
    host.querySelectorAll<HTMLElement>('[data-col-key]').forEach((cell) => {
      if (cell.getAttribute('data-col-key') !== key) return
      const range = document.createRange()
      range.selectNodeContents(cell)
      const style = getComputedStyle(cell)
      const padding = (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0)
      widest = Math.max(widest, range.getBoundingClientRect().width + padding)
    })
    if (widest <= 0) return
    onResizeColumn(key, Math.max(MIN_COL_PX, Math.min(MAX_COL_PX, Math.ceil(widest) + 2)))
  }
  const [wideCompactActive, setWideCompactActive] = useState(false)
  /* `cards-wide` answers a question CSS cannot express without copying the
     entire card anatomy under a second container query: when THIS grid's box
     crosses the measured 900px threshold, activate the already-canonical card
     recipe. ResizeObserver measures the component rather than the window, so a
     grid inside a drawer or split behaves from the space it actually owns.
     The first measurement runs in layout effect to avoid painting the wide
     table before its card form. */
  useLayoutEffect(() => {
    if (compact !== 'cards-wide') {
      setWideCompactActive(false)
      return
    }
    const node = rootRef.current
    if (!node) return
    const measure = (width = node.getBoundingClientRect().width || node.clientWidth) => {
      if (width <= 0) return
      setWideCompactActive((current) => {
        const next = width <= 900
        return current === next ? current : next
      })
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === node)
      measure(entry?.contentRect.width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [compact])
  /* The legacy `compact="cards"` fallback reflows under a 640px CONTAINER
     query, which CSS answers and JS did not know — so the header it hides was
     still in the tab order, its ⋮ and sort buttons focusable under the cards
     (WCAG 2.4.11). Measure the same box, so the header can be made inert. */
  const [narrowCompactActive, setNarrowCompactActive] = useState(false)
  useLayoutEffect(() => {
    if (compact !== 'cards' || presentation) { setNarrowCompactActive(false); return }
    const node = rootRef.current
    if (!node) return
    const measure = (width = node.getBoundingClientRect().width || node.clientWidth) => {
      if (width <= 0) return
      setNarrowCompactActive((current) => (width <= 640) === current ? current : width <= 640)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      measure(entries.find((candidate) => candidate.target === node)?.contentRect.width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [compact, presentation])
  /** The header is visually replaced by the cards: its controls go inert. */
  const headersHidden = presentation === 'cards' || wideCompactActive || narrowCompactActive
  const reportedHidden = useRef<boolean | null>(null)
  useEffect(() => {
    if (reportedHidden.current === headersHidden) return
    reportedHidden.current = headersHidden
    onHeadersHiddenChange?.(headersHidden)
  }, [headersHidden, onHeadersHiddenChange])
  const closeMenu = () => {
    pendingRestore = menu
      ? { sel: menu.kind === 'col' ? `[data-colmenu="${cssEscape(menu.col)}"]` : `[data-cell="${cssEscape(`${menu.rowId}:${menu.col}`)}"]`, at: Date.now() }
      : null
    setMenu(null)
  }
  /* No dependency array ON PURPOSE: the commit that can finally satisfy the
     request is often not the one that closed the menu, it is the one that
     re-mounts the grid when the refetch lands. */
  useEffect(() => {
    if (!pendingRestore || menu != null) return
    if (Date.now() - pendingRestore.at > RESTORE_WINDOW_MS) { pendingRestore = null; return }
    const el = rootRef.current?.querySelector<HTMLElement>(pendingRestore.sel)
    if (el) { pendingRestore = null; el.focus() }
  })
  const cellMenuRow = menu?.kind === 'cell' ? orderedRows.find((d) => String(d.id) === menu.rowId) : null

  /* Returns the write (#2149). A cell menu that opens a governed picker hands
     this back to `Lookup` as its `onPick` result, so the popover stays up until
     the cell is actually written and a refusal leaves it open. It used to be
     `void`ed, which left the caller nothing to wait on. */
  const commitCell = (row: GridRow, field: string, value: unknown) => {
    setEditing(null)
    if (!onCommitCell) return undefined
    return onCommitCell(row, field, value).then((written) => {
      if (!written) return
      setFlash(`${String(row.id)}:${field}`)
      setTimeout(() => setFlash(null), 850)
    })
  }

  // ── keyboard access for list rows (UX-02) ─────────────────────────────────
  const onRowKey = (e: ReactKeyboardEvent, i: number, id: string) => {
    // Do NOT hijack keys that belong to something inside the row. Inline editing
    // puts an <input> in a cell, and Enter there must COMMIT THE EDIT.
    const t = e.target as HTMLElement
    if (t !== e.currentTarget) {
      const recordLink = t.closest<HTMLElement>('[data-record-link]')
      if (recordLink && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault()
        e.stopPropagation()
        const row = recordLink.closest<HTMLElement>('[role="row"]')
        if (e.key === 'ArrowLeft') row?.focus()
        else row?.querySelector<HTMLElement>('[data-editcell]')?.focus()
        return
      }
      const interactive = t.closest('a, input, textarea, select, button, [contenteditable="true"], [role="menu"]')
      if (interactive) return
    }
    const move = (to: number) => {
      const clamped = Math.max(0, Math.min(orderedRows.length - 1, to))
      setActiveRow(clamped)
      const el = (e.currentTarget as HTMLElement).parentElement
        ?.querySelector<HTMLElement>(`[data-rowindex="${clamped}"]`)
      el?.focus()
      el?.scrollIntoView({ block: 'nearest' })
    }
    switch (e.key) {
      // Space would scroll the page out from under the selection.
      case 'Enter': case ' ': e.preventDefault(); onActivateRow?.(id); break
      case 'ArrowDown': e.preventDefault(); move(i + 1); break
      case 'ArrowUp': e.preventDefault(); move(i - 1); break
      case 'Home': e.preventDefault(); move(0); break
      case 'End': e.preventDefault(); move(orderedRows.length - 1); break
      case 'PageDown': e.preventDefault(); move(i + 10); break
      case 'PageUp': e.preventDefault(); move(i - 10); break
      // Into the row's interactive cells (#388).
      case 'ArrowRight': {
        e.preventDefault()
        ;(e.currentTarget as HTMLElement).querySelector<HTMLElement>('[data-record-link], [data-editcell]')?.focus()
        break
      }
    }
  }

  // ── keyboard entry into INLINE EDIT (#388 / UX-03) ──────────────────────────
  const onCellKey = (e: ReactKeyboardEvent, ctx: GridCellCtx, colCfg: GridColumn) => {
    if (e.target !== e.currentTarget) return   // keys inside the edit input belong to it
    const cell = e.currentTarget as HTMLElement
    const row = cell.closest<HTMLElement>('[role="row"]')
    const cells = row ? Array.from(row.querySelectorAll<HTMLElement>('[data-editcell]')) : []
    const i = cells.indexOf(cell)
    switch (e.key) {
      case 'Enter': case 'F2':
        e.preventDefault(); e.stopPropagation()
        if (colCfg.editable) ctx.startEdit(); else if (colCfg.hasMenu) ctx.openMenu(cell)
        break
      case 'ArrowRight': e.preventDefault(); e.stopPropagation(); cells[i + 1]?.focus(); break
      case 'ArrowLeft': e.preventDefault(); e.stopPropagation(); (cells[i - 1] ?? row)?.focus(); break
      case 'Escape': e.preventDefault(); e.stopPropagation(); row?.focus(); break
    }
  }

  const cellFor = (d: GridRow, colCfg: GridColumn, colIndex: number) => {
    const c = colCfg.key
    const isEd = !!editing && editing.id === String(d.id) && editing.field === c
    const flashing = flash === `${String(d.id)}:${c}`
    const ctx: GridCellCtx = {
      editing: isEd, flashing,
      startEdit: () => { editCancelled.current = false; setEditing({ id: String(d.id), field: c }) },
      commit: (v) => commitCell(d, c, v),
      openMenu: (anchor) => setMenu({ kind: 'cell', anchor, col: c, rowId: String(d.id) }),
    }
    const body = colCfg.cell ? colCfg.cell(d, ctx) : <span>{String(d[c] ?? '')}</span>
    const clickable = colCfg.editable || colCfg.hasMenu
    return (
      // role=gridcell: a role=row whose children are plain divs is an invalid
      // grid, and a screen reader reads the row as one run-on string with no
      // column boundaries (UX-05).
      <div key={c} role="gridcell" data-cell={`${String(d.id)}:${c}`} aria-colindex={colIndex}
        data-col-key={c} {...pinnedCellProps(c)}
        data-cell-label={colCfg.label} data-cell-kind={colCfg.compact ?? 'detail'}
        data-emphasis={colCfg.emphasis}
        // Interactive cells are focusable (via ArrowRight from the row) and say
        // what Enter/F2 will do — the title feeds the accessible DESCRIPTION
        // computation, so a screen reader hears it too (#388 / UX-06).
        tabIndex={clickable ? -1 : undefined}
        data-editcell={clickable ? '' : undefined}
        title={colCfg.editable ? 'Editable — press Enter or F2 to edit'
          : colCfg.hasMenu ? 'Press Enter or F2 to open the menu' : undefined}
        onKeyDown={clickable ? (e) => onCellKey(e, ctx, colCfg) : undefined}
        className={cx(s.cell, colCfg.align === 'right' && s.cellR, clickable && s.cellEdit, flashing && s.flash)}
        onClick={colCfg.editable ? (e) => { e.stopPropagation(); ctx.startEdit() }
          : colCfg.hasMenu ? (e) => { e.stopPropagation(); ctx.openMenu(e.currentTarget as HTMLElement) } : undefined}>
        {body}
        {isEd && colCfg.editable && (
          <div className={s.ed} onClick={(e) => e.stopPropagation()}>
            {colCfg.editKind === 'date' ? (
              <DatePicker autoFocus value={String(d[c] ?? '')}
                onChange={(raw) => { void ctx.commit(raw); setEditing(null) }}
                onBlur={() => setEditing(null)}
                onKeyDown={(e) => {
                  const cell = e.currentTarget.closest<HTMLElement>('[role="gridcell"]')
                  if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); cell?.focus() }
                }} />
            ) : <input autoFocus defaultValue={String(d[c] ?? '')} type="text"
              style={colCfg.editKind === 'number' ? { textAlign: 'right' } : undefined}
              onKeyDown={(e) => {
                // Commit and cancel both RETURN FOCUS to the cell — without it a
                // keyboard edit strands focus on <body> and the roving grid is lost.
                const cell = (e.target as HTMLElement).closest<HTMLElement>('[role="gridcell"]')
                if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); cell?.focus() }
                if (e.key === 'Escape') { e.stopPropagation(); editCancelled.current = true; setEditing(null); cell?.focus() }
              }}
              /* Blur-commit has no surface to hold: the editor is already gone
                 by the time this fires, and the cell's own flash reports the
                 write. Explicitly discarded rather than left floating (#2149). */
              onBlur={(e) => { if (!editing || editCancelled.current) return; const raw = e.target.value; const floor = colCfg.editMin ?? 0; void ctx.commit(colCfg.editKind === 'number' ? Math.max(floor, Number(raw.replace(/[^0-9.]/g, '')) || floor) : raw) }} />}
          </div>
        )}
      </div>
    )
  }

  /* ALWAYS VISIBLE (#2375): the column's options are how a person filters,
     groups, hides and restores — a control that appears only on hover is a
     control a touch or keyboard user never learns exists. */
  const menuButton = (colCfg: GridColumn) => renderColumnMenu && colCfg.menuable !== false ? (

      <button type="button" className={s.colbtn} data-colmenu={colCfg.key}
        data-testid={`grid-col-${colCfg.key}-menu`}
        aria-haspopup="menu"
        aria-expanded={openColumnMenu === colCfg.key || (menu?.kind === 'col' && menu.col === colCfg.key)}
        aria-label={colCfg.filtered
          ? `${colCfg.label} column options — ${colCfg.filterSummary ?? `${colCfg.label} is filtered`}`
          : `${colCfg.label} column options`}
        onClick={(e) => { e.stopPropagation(); setMenu({ kind: 'col', anchor: e.currentTarget, col: colCfg.key }) }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault(); e.stopPropagation()
            setMenu({ kind: 'col', anchor: e.currentTarget, col: colCfg.key })
          }
        }}>
        <EllipsisVertical size={14} aria-hidden="true" />
      </button>
    
  ) : null

  return (
    <div ref={rootRef} className={s.root} data-any={sel.size > 0 || undefined}
      data-pinned-cols={pinnedKeys.length > 0 || undefined}
      style={{ ...(hasLead ? { ['--grid-active-lead' as string]: leadWidth } : {}), ['--grid-row-min' as string]: rowMinimum }}
      data-grouped={grouping ? grouping.column : undefined}
      data-presentation={presentation}
      data-compact={compact}
      data-compact-active={wideCompactActive || undefined}
      data-wrap={wrapCells || undefined}>
      {/* role=grid because the rows ARE arrow-navigable (UX-02).

          TREEGRID when the rows expand, grid when they do not. `aria-expanded`
          is only defined on a row inside a treegrid — on a plain grid row it is
          a serious axe violation (aria-conditional-attr). A list whose rows open
          an expansion IS a treegrid; that is the role's whole definition. */}
      <div role={canExpand ? 'treegrid' : 'grid'}
        aria-rowcount={1 + renderedRowCount}
        aria-colcount={columns.length + (hasLead ? 1 : 0)}>
        <div ref={headRef} className={s.ghead} role="row" aria-rowindex={1} style={{ gridTemplateColumns: template }}
          inert={headersHidden || undefined} data-headers-hidden={headersHidden || undefined}>
          {/* The header's lead cell holds select-all in slot 1 and NOTHING else
              (#2375): no placeholders for expand or pin, because the slots are
              start-aligned from the same inset, so slot 1 lines up with every
              row's checkbox without them. */}
          {hasLead && <div className={cx(s.hcell, s.leadCell)} role="columnheader" aria-colindex={1}
            aria-label={selectable ? undefined : 'Row controls'}>
            {selectable && (
              <span className={s.slot} data-grid-slot="select" data-grid-selection-slot="">
                {/* An unnamed checkbox is announced as just "checkbox" — the one
                    control that acts on EVERYTHING needs a name most (UX-06). */}
                <input ref={selectAllRef} type="checkbox" className={s.checkHit}
                  aria-label={`Select all ${itemsLabel}`} data-testid="grid-select-all"
                  checked={allOnPageSelected}
                  onChange={() => {
                    // Selection is host-owned and may span pages. Select-all is
                    // therefore scoped to the visible page's DATA rows — never a
                    // group header — union its ids in, or remove only those ids.
                    // Replacing/clearing the whole Set silently lost work as soon
                    // as a user paged (#882).
                    const next = new Set(sel)
                    for (const id of orderedIds) {
                      if (allOnPageSelected) next.delete(id)
                      else next.add(id)
                    }
                    onSelectionChange!(next)
                  }} />
                <span className={s.checkBox} aria-hidden="true" />
              </span>
            )}
          </div>}
          {columns.map((colCfg, ci) => (
            // columnheader + aria-sort so a screen reader announces WHICH
            // column the list is ordered by, and in which direction (UX-05).
            <div key={colCfg.key} role="columnheader" aria-colindex={ci + firstCol}
              data-col-key={colCfg.key} {...pinnedCellProps(colCfg.key)}
              aria-sort={colCfg.sort ?? 'none'}
              className={cx(s.hcell, colCfg.align === 'right' && s.hcellR)}
              data-filtered={colCfg.filtered || undefined}
              data-menu-open={openColumnMenu === colCfg.key || undefined}
              data-drag-target={dragTarget === ci || undefined}
              onDragOver={onMoveColumn ? (event) => {
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                setDragTarget(ci)
              } : undefined}
              onDrop={onMoveColumn ? (event) => {
                event.preventDefault()
                const key = draggingColumn || event.dataTransfer.getData('text/plain')
                if (key) moveColumnByKey(key, ci)
                setDraggingColumn(null)
                setDragTarget(null)
              } : undefined}>
              {/* A right-aligned column draws its ⋮ FIRST, so the label sits over
                  its numbers and DOM order stays the visual order (no `order`). */}
              {colCfg.align === 'right' && menuButton(colCfg)}
              {/* The drag handle takes NO layout space (#2375): it sits on the
                  cell's leading edge and appears on hover or focus, so a label
                  starts at the same inset as the values under it. */}
              {onMoveColumn && (
                <button type="button" className={s.moveHandle} draggable
                  aria-label={`Move ${colCfg.label} column`}
                  title="Drag to reorder; use Left and Right arrow keys for precise movement"
                  onDragStart={(event) => {
                    event.stopPropagation()
                    event.dataTransfer.effectAllowed = 'move'
                    event.dataTransfer.setData('text/plain', colCfg.key)
                    setDraggingColumn(colCfg.key)
                    setDragTarget(ci)
                  }}
                  onDragEnd={() => { setDraggingColumn(null); setDragTarget(null) }}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowLeft') { event.preventDefault(); moveColumnByKey(colCfg.key, ci - 1) }
                    if (event.key === 'ArrowRight') { event.preventDefault(); moveColumnByKey(colCfg.key, ci + 1) }
                    if (event.key === 'Home') { event.preventDefault(); moveColumnByKey(colCfg.key, 0) }
                    if (event.key === 'End') { event.preventDefault(); moveColumnByKey(colCfg.key, columns.length - 1) }
                  }}><GripVertical size={12} aria-hidden="true" /></button>
              )}
              {/* THE LABEL IS THE SORT (#2375). A real <button>, not a clickable
                  span: sorting is a primary action and it was mouse-only on
                  every surface once (WCAG 2.1.1). Shift adds a key. */}
              {onSortColumn && colCfg.sortable !== false ? (
                <button type="button" className={s.hlabel} data-testid={`grid-col-${colCfg.key}-sort`}
                  aria-label={`Sort by ${colCfg.label}${multiSort && colCfg.sortOrder ? `, priority ${colCfg.sortOrder}` : ''}. Hold Shift to add to the current order`}
                  onClick={(event) => onSortColumn(colCfg.key, { additive: event.shiftKey })}>
                  <span className={s.hlabelText}>{colCfg.label}</span>
                  {colCfg.sort === 'ascending' || colCfg.sort === 'descending' ? (
                    <span className={s.sortArrow} aria-hidden="true">{colCfg.sort === 'ascending' ? '↑' : '↓'}</span>
                  ) : null}
                  {multiSort && colCfg.sortOrder && colCfg.sort && colCfg.sort !== 'none'
                    ? <span className={s.sortOrder} aria-hidden="true">{colCfg.sortOrder}</span>
                    : null}
                </button>
              ) : (
                <span className={cx(s.hlabel, s.hlabelStatic)}><span className={s.hlabelText}>{colCfg.label}</span></span>
              )}
              {/* A filtered column must LOOK filtered, in something other than a
                  hue. The mark is a glyph with a title; the words live on the ⋮'s
                  accessible name, which is where a screen-reader user meets this
                  column's controls. */}
              {colCfg.filtered && (
                <span className={s.filterMark} data-testid={`col-filtered-${colCfg.key}`}
                  title={colCfg.filterSummary ?? `Filtered by ${colCfg.label}`} aria-hidden="true">
                  <FilterGlyph size={11} strokeWidth={2.25} />
                </span>
              )}
              {colCfg.align !== 'right' && menuButton(colCfg)}
              {onResizeColumn && (
                <ColumnResizer
                  column={colCfg.key}
                  label={colCfg.label}
                  width={pxWidth(colCfg)}
                  onResize={(px) => onResizeColumn(colCfg.key, px)}
                  onReset={() => onResetColumn?.(colCfg.key)}
                />
              )}
            </div>
          ))}
        </div>

        {layout.map((entry) => {
          if (entry.kind === 'group') {
            const total = grouping!.totalOf(entry.key)
            const continued = grouping!.continued?.has(entry.key) ?? false
            const label = grouping!.labelOf(entry.key)
            const count = total == null ? '' : `${total} ${total === 1 ? 'row' : 'rows'}`
            /* A group header is a PRESENTATION row (#2375): not selectable, not
               in the roving order (no `data-rowindex`, no tab stop), and not
               counted by select-all. It spans every column so a reader hears
               one heading, not an empty cell per column. */
            return (
              <div key={`group:${entry.key}:${entry.at}`} className={s.groupRow} role="row"
                aria-rowindex={entry.rowIndex} data-grid-group={entry.key}
                data-testid={`grid-group-${entry.key}`}
                aria-label={`${grouping!.columnLabel}: ${label}${count ? `, ${count}` : ''}${continued ? ', continued from the previous page' : ''}`}>
                <div className={s.groupCell} role="gridcell" aria-colindex={1}
                  aria-colspan={columns.length + (hasLead ? 1 : 0)}>
                  <span className={s.groupLabel}>{label}</span>
                  <span className={cx(s.groupCount, 'tnum')} data-testid={`grid-group-${entry.key}-total`}>
                    {total == null ? '…' : total}
                  </span>
                  {continued && <span className={s.groupContinued}>continued</span>}
                </div>
              </div>
            )
          }
          const { row: d, index: i, rowIndex } = entry
          const id = String(d.id)
          const rowPinned = pinRank.has(id)
          const rowCanExpand = renderExpansion != null && (canExpandRow?.(d) ?? true)
          const deco = decorateRow?.(d)
          const flagCount = deco?.flagCount ?? 0
          const hl = deco?.tone
          return (
            <Fragment key={id}>
              <div className={s.grow} style={{ gridTemplateColumns: template }}
                role="row" aria-rowindex={rowIndex}
                // The row's accessible NAME and its selected state (UX-05).
                // Without aria-selected a screen-reader user tabbing a
                // multi-select grid has no way to know what is in the selection
                // they are about to act on.
                aria-selected={selectable ? sel.has(id) : undefined}
                aria-label={`${rowName(d)}${rowPinned ? ', pinned' : ''}`}
                // `aria-expanded` describes the row's OWN expansion — the only
                // thing the attribute means on a treegrid row, and undefined on
                // a plain grid row at all.
                aria-expanded={rowCanExpand ? expandedRow === id : undefined}
                aria-controls={rowControlsId}
                data-rowindex={i}
                // Roving tabindex (UX-02): ONE tab stop enters the list, arrows
                // move within it. Every row being tabbable is its own problem —
                // hundreds of stops before the next control.
                tabIndex={rowsNavigable ? (i === activeRowOnPage ? 0 : -1) : undefined}
                onFocus={rowsNavigable ? () => setActiveRow(i) : undefined}
                onKeyDown={rowsNavigable ? (e) => onRowKey(e, i, id) : undefined}
                data-actionable={onActivateRow ? '' : undefined}
                data-pinned={rowPinned || undefined}
                data-on={activeRowId === id || undefined} data-sel={sel.has(id) || undefined}
                data-hl={hl || undefined} data-flags={flagCount || undefined} title={deco?.title}
                onClick={onActivateRow ? () => onActivateRow(id) : undefined}>
                {/* THE LEAD: fixed slots, fixed order — select · expand · pin —
                    start-aligned from the inset (#2375). A slot is omitted only
                    when the CAPABILITY is absent from the grid; a row that
                    cannot expand in a grid that can keeps an empty slot, so its
                    pin lines up with its neighbours'. */}
                {hasLead && <div className={cx(s.cell, s.leadCell)} role="gridcell" aria-colindex={1}
                  data-grid-lead="">
                  {selectable && (
                    <span className={s.slot} data-grid-slot="select" data-grid-selection-slot="">
                      <input type="checkbox" className={s.checkHit} aria-label={`Select ${rowName(d)}`}
                        data-testid={`grid-row-${id}-select`}
                        checked={sel.has(id)} onClick={(e) => e.stopPropagation()}
                        onChange={() => { const n = new Set(sel); if (n.has(id)) n.delete(id); else n.add(id); onSelectionChange!(n) }} />
                      <span className={s.checkBox} aria-hidden="true" />
                    </span>
                  )}
                  {canExpand && (rowCanExpand ? (
                    <button type="button" className={cx(s.slot, s.expand)} data-grid-slot="expand"
                      data-testid={expandTestId?.(d) ?? `band-twist-row-${id}`}
                      aria-expanded={expandedRow === id}
                      aria-label={expandLabel
                        ? expandLabel(d, expandedRow === id)
                        : expandedRow === id
                          ? `Hide what ${rowName(d)} is related to`
                          : `Show what ${rowName(d)} is related to`}
                      onClick={(e) => { e.stopPropagation(); setExpandedRow(expandedRow === id ? null : id) }}>
                      <ChevronRight className={s.leadGlyph} data-grid-glyph="expansion" size={14} strokeWidth={2.25} aria-hidden="true" />
                    </button>
                  ) : <span className={s.slot} data-grid-slot="expand" aria-hidden="true" />)}
                  {pinnable && (
                    <button type="button" className={cx(s.slot, s.rowPin)} data-grid-slot="pin"
                      data-testid={`grid-row-${id}-pin`}
                      aria-pressed={rowPinned}
                      aria-label={`${rowPinned ? 'Unpin' : 'Pin'} ${rowName(d)}`}
                      title={`${rowPinned ? 'Unpin' : 'Pin'} ${rowName(d)}`}
                      onClick={(event) => {
                        event.stopPropagation()
                        const next = pinnedRowIds.filter((pin) => pin !== id)
                        if (!rowPinned) next.push(id)
                        onPinnedRowIdsChange(next)
                      }}>
                      <Pin className={s.leadGlyph} data-grid-glyph="pin" size={14} strokeWidth={2}
                        fill={rowPinned ? 'currentColor' : 'none'} aria-hidden="true" />
                    </button>
                  )}
                </div>}
                {columns.map((colCfg, ci) => cellFor(d, colCfg, ci + firstCol))}
              </div>
              {expandedRow === id && (
                /* A grid may only contain rows, and a row only gridcells — so the
                   expansion is a ROW carrying ONE cell that spans every column,
                   not a div smuggled between rows. It is skipped by the roving
                   tabindex (tabIndex -1, no data-rowindex): arrows move between
                   RECORDS, Tab enters the expansion. */
                <div className={s.bandRow} role="row" aria-rowindex={rowIndex + 1} tabIndex={-1}>
                  <div className={s.expansion} data-grid-expansion="" role="gridcell"
                    aria-colindex={1} aria-colspan={columns.length + (hasLead ? 1 : 0)}>
                    {renderExpansion!(d)}
                  </div>
                </div>
              )}
            </Fragment>
          )
        })}
      </div>{/* /role=grid */}

      {footer}

      {menu?.kind === 'col' && renderColumnMenu?.({
        column: menu.col, anchor: menu.anchor, close: closeMenu,
        autosize: onResizeColumn ? () => autosizeColumn(menu.col) : undefined,
      })}
      {menu?.kind === 'cell' && cellMenuRow && renderCellMenu?.({
        row: cellMenuRow, column: menu.col, anchor: menu.anchor, close: closeMenu,
        commit: (v) => commitCell(cellMenuRow, menu.col, v),
      })}
    </div>
  )
}

/**
 * A pending "put focus back where it was" request, at MODULE scope because the
 * grid that made it may not be the instance that answers it — see `closeMenu`.
 * One at a time by construction: a grid has one menu open at a time, and the
 * request is consumed by the first matching node rendered after it.
 */
let pendingRestore: { sel: string; at: number } | null = null
/** After this long the request is abandoned rather than answered by a page the
 *  user has since navigated to. */
const RESTORE_WINDOW_MS = 5_000

/** `CSS.escape` where it exists (every browser this ships to); a conservative
 *  fallback for jsdom and for `react-dom/server`, where no menu is ever open. */
const cssEscape = (v: string): string =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(v) : v.replace(/["\\]/g, '\\$&')

/** Stable identity for the uncontrolled case — a fresh Set per render would
 *  make every memo downstream of `selection` useless. */
const EMPTY: Set<string> = new Set()
